import { jsonBody } from '@dashboard/module-contract/http';
import { refusalReasonOf, withStepUp } from '@dashboard/module-contract/client';
/**
 * The one place this app talks to its API. Same origin in every environment —
 * CloudFront serves the SPA and routes `/api/*` to the Lambda, and the Vite
 * dev server proxies the same prefix — so there is no base URL to configure
 * and no CORS to get wrong.
 */

export class ApiError extends Error {
	constructor(
		readonly status: number,
		/** The API's own `error` field where it sent one, else a generic code. */
		readonly code: string,
		/**
		 * Carried so a failure can be explained differently for a read and a
		 * write. A read that fails changed nothing and can simply be repeated;
		 * a write that failed partway may already have taken effect.
		 */
		readonly method: string = 'GET',
	) {
		super(`${String(status)} ${code}`);
		this.name = 'ApiError';
	}
}

export function isWrite(method: string): boolean {
	return method.toUpperCase() !== 'GET';
}

/**
 * The deployment requires an enrolled authenticator for writes and the caller
 * has none (`016`). No code clears it — the remedy is to enrol, which is why
 * this is told apart from a step-up rather than folded into it.
 */
export function needsAuthenticator(error: unknown): boolean {
	return error instanceof ApiError && error.status === 403 && error.code === 'mfa-required';
}

/**
 * Asked for when a write is refused because the write window has lapsed.
 *
 * **Registered once, by the shell**, and now genuinely consulted from every
 * client rather than only this one. The registry moved to
 * `@dashboard/module-contract/client`, because the claim this comment used to
 * make — "every write in the core and in every module gets the same
 * treatment" — was false: a module cannot import from `apps/ui`, so each one
 * had its own `request` and no step-up handling at all. Under
 * `requireMfaForWrites` every module write was refused and reported as "That
 * did not work. Try again."
 *
 * That is `D90` for the second time, on the same three files. This time the
 * seam is somewhere a module can reach.
 */
export { onStepUpRequired, resetStepUpHandler } from '@dashboard/module-contract/client';
export type { RefusalReason, StepUpHandler } from '@dashboard/module-contract/client';

interface RequestOptions {
	readonly method?: string;
	readonly body?: unknown;
	readonly signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	return withStepUp(
		() => send<T>(path, options),
		(error) =>
			error instanceof ApiError ? refusalReasonOf(error.status, error.code) : undefined,
	);
}

async function send<T>(path: string, options: RequestOptions): Promise<T> {
	const method = options.method ?? 'GET';

	/**
	 * **A `DELETE` may not carry a body** (`D138`), the same rule the module
	 * client holds — and here for the same reason that client exists at all:
	 * what one of them learns, the other goes without.
	 *
	 * CloudFront's origin access control signs the payload hash this client
	 * computes, and a `DELETE` body does not reach the origin, so the hashes
	 * disagree and the function URL rejects the signature. Nothing in the core
	 * does this today; the rule is here so that nothing starts.
	 */
	if (method.toUpperCase() === 'DELETE' && options.body !== undefined) {
		throw new Error(
			`DELETE ${path} cannot carry a body: CloudFront does not sign one, and the request fails only once deployed. Name what to remove in the path or the query instead.`,
		);
	}

	/**
	 * The content type and the payload hash CloudFront will not compute, from
	 * the one place that knows about either — a module's client needs exactly
	 * the same thing, and having two of them is how one of them went without.
	 */
	const sending = options.body === undefined ? undefined : await jsonBody(options.body);

	const response = await fetch(path, {
		method,
		// The session cookie is `HttpOnly` and host-only; this is what makes
		// the browser attach it. Nothing reads it from script, by design.
		credentials: 'same-origin',
		headers: { Accept: 'application/json', ...sending?.headers },
		...(sending === undefined ? {} : { body: sending.body }),
		...(options.signal === undefined ? {} : { signal: options.signal }),
	});

	if (response.status === 204) return undefined as T;

	const payload: unknown = await response.json().catch(() => undefined);

	if (!response.ok) {
		throw new ApiError(response.status, errorCode(payload), method);
	}

	return payload as T;
}

/**
 * The API answers failures as `{ error: '<code>' }`. Anything else — a
 * CloudFront error page, a truncated body — is still a failure, so it gets a
 * code rather than being allowed to read as success.
 */
function errorCode(payload: unknown): string {
	if (typeof payload === 'object' && payload !== null && 'error' in payload) {
		// `'error' in payload` has already narrowed it; no assertion needed.
		const { error } = payload;
		if (typeof error === 'string' && error !== '') return error;
	}
	return 'unknown_error';
}
